'use client'

/**
 * QuickActionsContext
 *
 * Single source of truth for quick actions. Provider fetches once on mount
 * and runs default-tools init at most once; all consumers share the same state
 * to avoid N GET /api/quick-actions per page (e.g. one per SidebarItemMenu).
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
  type ReactNode
} from 'react'
import { usePathname } from 'next/navigation'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase/browser-client'

export interface QuickAction {
  id: string
  tool_id: string
  tool_name: string
  tool_description: string
  tool_description_short: string
  item_position: number
  created_at: string
}

export interface QuickActionsContextValue {
  quickActions: QuickAction[]
  isLoading: boolean
  error: string | null
  addQuickAction: (toolId: string, position?: number) => Promise<boolean>
  removeQuickAction: (toolId: string) => Promise<boolean>
  reorderQuickActions: (newOrder: QuickAction[]) => Promise<boolean>
  isToolInQuickActions: (toolId: string) => boolean
  refreshQuickActions: () => Promise<void>
}

const QuickActionsContext = createContext<QuickActionsContextValue | null>(null)

const DEFAULT_TOOLS_VERSION = 'v4'
const QUICK_ACTIONS_IDLE_TIMEOUT_MS = 4_000
const QUICK_ACTIONS_FALLBACK_DELAY_MS = 1_000
const SPACE_ROUTE_QUICK_ACTIONS_DELAY_MS = 12_000

function truncateDescription(description: string, maxLength: number = 50): string {
  if (!description || description.length <= maxLength) return description || ''
  return description.substring(0, maxLength).trim() + '...'
}

export function QuickActionsProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const [quickActions, setQuickActions] = useState<QuickAction[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const defaultsInitRanRef = useRef(false)
  const isInitializingRef = useRef(false)
  const latestQuickActionsRef = useRef<QuickAction[]>(quickActions)

  latestQuickActionsRef.current = quickActions

  const fetchQuickActions = useCallback(async () => {
    if (!isAuthenticated) {
      setQuickActions([])
      setError(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/quick-actions')
      if (response.status === 401) {
        setQuickActions([])
        return
      }

      if (!response.ok) throw new Error('Failed to fetch quick actions')

      const data = await response.json()
      const list = (data.quickActions || []).map((qa: Record<string, unknown>) => ({
        ...qa,
        tool_description_short: truncateDescription(String(qa.tool_description ?? ''), 50)
      }))
      setQuickActions(list)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  // Sync quick actions with auth state transitions
  useEffect(() => {
    let mounted = true

    ;(async () => {
      const {
        data: { session }
      } = await supabase.auth.getSession()

      if (!mounted) return

      const nextIsAuthenticated = Boolean(session?.user)
      setIsAuthenticated(nextIsAuthenticated)
      defaultsInitRanRef.current = false
      isInitializingRef.current = false
    })()

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextIsAuthenticated = Boolean(session?.user)
      setIsAuthenticated(nextIsAuthenticated)
      defaultsInitRanRef.current = false
      isInitializingRef.current = false
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    let isCancelled = false
    let idleCallbackId: number | null = null
    let timeoutId: ReturnType<typeof window.setTimeout> | null = null

    const loadQuickActions = () => {
      if (isCancelled) {
        return
      }

      void fetchQuickActions()
    }

    if (pathname?.startsWith('/spaces')) {
      timeoutId = window.setTimeout(loadQuickActions, SPACE_ROUTE_QUICK_ACTIONS_DELAY_MS)
    } else if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(() => {
        loadQuickActions()
      }, { timeout: QUICK_ACTIONS_IDLE_TIMEOUT_MS })
    } else {
      timeoutId = window.setTimeout(loadQuickActions, QUICK_ACTIONS_FALLBACK_DELAY_MS)
    }

    return () => {
      isCancelled = true

      if (idleCallbackId !== null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId)
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [fetchQuickActions, pathname])

  // Initialize defaults at most once after first load (empty list or version bump)
  useEffect(() => {
    if (!isAuthenticated || isLoading || isInitializingRef.current || defaultsInitRanRef.current) {
      return
    }

    const storedVersion = localStorage.getItem('quickActionsDefaultVersion')
    const needsVersionUpdate = storedVersion !== DEFAULT_TOOLS_VERSION
    const isEmpty = quickActions.length === 0
    if (!isEmpty && !needsVersionUpdate) return

    defaultsInitRanRef.current = true
    isInitializingRef.current = true

    const defaultToolIds = [
      'b3f07a6e-5e01-423e-1f05-ee51830608ff',
      'b3f07a6e-5e01-423e-9f06-ee51830608be',
      'b3f07a6e-5e01-423e-1f05-ee51830608gf',
      'b3f07a6e-5e01-423e-1f05-ee51830608vc',
      'audio-tool-v1',
      '3d-asset-generator-v1'
    ]

    let mounted = true
    ;(async () => {
      try {
        for (let i = 0; i < defaultToolIds.length && mounted; i++) {
          const toolId = defaultToolIds[i]
          const existing = quickActions.some((qa) => qa.tool_id === toolId)
          if (existing) continue
          const response = await fetch('/api/quick-actions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ toolId, position: i })
          })
          if (!response.ok) continue
        }
        if (mounted) await fetchQuickActions()
        if (mounted) localStorage.setItem('quickActionsDefaultVersion', DEFAULT_TOOLS_VERSION)
      } finally {
        if (mounted) isInitializingRef.current = false
      }
    })()

    return () => {
      mounted = false
    }
  }, [isAuthenticated, isLoading, quickActions.length, fetchQuickActions])

  const addQuickAction = useCallback(async (toolId: string, position?: number): Promise<boolean> => {
    if (!toolId || typeof toolId !== 'string') return false
    try {
      const response = await fetch('/api/quick-actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toolId, position })
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error((errorData as { error?: string }).error || 'Failed to add quick action')
      }
      const data = await response.json()
      const newQuickAction: QuickAction = {
        id: (data.quickAction as { id: string }).id,
        tool_id: toolId,
        tool_name: (data.tool as { name: string }).name,
        tool_description: (data.tool as { description?: string }).description || '',
        tool_description_short: truncateDescription((data.tool as { description?: string }).description || '', 50),
        item_position: (data.quickAction as { position: number }).position,
        created_at: (data.quickAction as { created_at: string }).created_at
      }
      setQuickActions((prev) => {
        const updated = [...prev, newQuickAction]
        return updated.sort((a, b) => a.item_position - b.item_position)
      })
      toast.success('Added to Quick Actions')
      return true
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add quick action'
      setError(msg)
      toast.error(msg)
      return false
    }
  }, [])

  const removeQuickAction = useCallback(async (toolId: string): Promise<boolean> => {
    if (!toolId || typeof toolId !== 'string') return false
    try {
      const response = await fetch(`/api/quick-actions?toolId=${encodeURIComponent(toolId)}`, {
        method: 'DELETE'
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error((errorData as { error?: string }).error || 'Failed to remove quick action')
      }
      setQuickActions((prev) => prev.filter((qa) => qa.tool_id !== toolId))
      toast.success('Removed from Quick Actions')
      return true
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove quick action'
      setError(msg)
      toast.error(msg)
      return false
    }
  }, [])

  const reorderQuickActions = useCallback(async (newOrder: QuickAction[]): Promise<boolean> => {
    const reordered = newOrder.map((qa, index) => ({ ...qa, item_position: index }))
    setQuickActions(reordered)
    try {
      const response = await fetch('/api/quick-actions', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quickActions: reordered })
      })
      if (!response.ok) {
        setQuickActions(latestQuickActionsRef.current)
        const errorData = await response.json()
        throw new Error((errorData as { error?: string }).error || 'Failed to reorder quick actions')
      }
      return true
    } catch (err) {
      setQuickActions(latestQuickActionsRef.current)
      const msg = err instanceof Error ? err.message : 'Failed to reorder quick actions'
      setError(msg)
      toast.error(msg)
      return false
    }
  }, [])

  const isToolInQuickActions = useCallback((toolId: string): boolean => {
    if (!toolId) return false
    return quickActions.some((qa) => qa.tool_id === toolId)
  }, [quickActions])

  const refreshQuickActions = useCallback(async () => {
    await fetchQuickActions()
  }, [fetchQuickActions])

  const value: QuickActionsContextValue = {
    quickActions,
    isLoading,
    error,
    addQuickAction,
    removeQuickAction,
    reorderQuickActions,
    isToolInQuickActions,
    refreshQuickActions
  }

  return (
    <QuickActionsContext.Provider value={value}>
      {children}
    </QuickActionsContext.Provider>
  )
}

export function useQuickActionsContext(): QuickActionsContextValue {
  const ctx = useContext(QuickActionsContext)
  if (!ctx) {
    throw new Error('useQuickActions must be used within QuickActionsProvider')
  }
  return ctx
}
